/**
 * Adobe events for agent experiments (TRAC-1108; Agent Experiments Plan, ticket 4).
 *
 * PENDING Josh's Adobe testing plan. The names below follow the chat's existing
 * lowercase "tr: ai tire agent: ..." links and ride the same route (hostAnalytics
 * postAnalyticsEvent). If Josh's plan renames an event or a field, change it here
 * and nowhere else; the call sites only use the keys.
 *
 * What reaches Adobe: the custom link itself carries only eVar92 (session id, with
 * the arm inside it) and prop49 (page name). The fields listed per event travel in
 * the data layer entry and the postMessage to the host page, not in the link. A
 * dedicated arm eVar/prop needs web's analytics team to allocate one. Never PII,
 * never free text from a prompt (promptId is a row id, not the prompt's words).
 */

export type ExperimentEventKey = 'experiment_exposed' | 'starter_prompt_click' | 'order_lookup_opened';

export interface ExperimentEventSpec {
  linkName: string;
  /** Extra fields this event carries beyond the envelope (for web's listener). */
  fields: readonly string[];
}

export const EXPERIMENT_EVENTS: Readonly<Record<ExperimentEventKey, ExperimentEventSpec>> = Object.freeze({
  // Once per session per experiment, when that arm's starter prompts first render.
  experiment_exposed: {
    linkName: 'tr: ai tire agent: experiment exposed',
    fields: ['experimentId', 'arm', 'pageBucket'],
  },
  // Each click on a starter prompt.
  starter_prompt_click: {
    linkName: 'tr: ai tire agent: starter prompt click',
    fields: ['experimentId', 'arm', 'pageBucket', 'promptKind', 'promptId'],
  },
  // The ZIP and email box opens. No order data, ever.
  order_lookup_opened: {
    linkName: 'tr: ai tire agent: order lookup opened',
    fields: [],
  },
});

/**
 * While an experiment is running, every chat event (response, capability links, live
 * chat, the three above) also carries these two, so web can filter any chat activity
 * by arm.
 */
export const EXPERIMENT_ENVELOPE_FIELDS = Object.freeze({ experimentId: 'experimentId', arm: 'arm' });

/**
 * The Adobe eVar that carries the experiment arm on its own, e.g. 'eVar93'.
 *
 * UNSET ON PURPOSE: the number is not allocated yet (Josh is asking web's analytics
 * team; see TRAC-358). While this is null the custom link sends exactly what it always
 * has (eVar92 = session id, with the arm already inside it, and prop49 = page name), and
 * the arm travels only in the data layer entry and the host postMessage. Once a number
 * is allocated, set it here and nowhere else: the data layer's analytics.evars and the
 * custom link then carry "<experimentId>:<arm>" in that eVar. Never eVar92.
 */
export const EXPERIMENT_ARM_ADOBE_EVAR: string | null = null;

const EVAR_NAME = /^eVar([1-9]\d{0,2})$/;

/**
 * The arm's eVar and its value for one event, or null when no eVar is configured, the
 * configured name is not a valid eVar (or is eVar92, the session id), or the event is
 * outside any experiment. The eVar name is a parameter only so tests can check both sides.
 */
export function armAdobeVar(
  experimentId: unknown,
  arm: unknown,
  evar: string | null = EXPERIMENT_ARM_ADOBE_EVAR,
): { name: `eVar${number}`; value: string } | null {
  if (!evar || typeof experimentId !== 'string' || typeof arm !== 'string') return null;
  if (!experimentId || !arm) return null;
  const m = EVAR_NAME.exec(evar);
  if (!m || evar === 'eVar92' || Number(m[1]) > 250) return null;
  return { name: evar as `eVar${number}`, value: `${experimentId}:${arm}` };
}

/**
 * The experiment letters whose arm decides which starter prompts a session sees. The
 * exposure event fires only for these, when that arm's prompts first render. "b" is
 * experiment 1, per-page starter prompts (TRAC-1107); until it ships no session is
 * assigned "b", so exposure stays silent (a local ?exp=b1 / b2 forces an arm).
 */
export const STARTER_PROMPT_EXPERIMENTS: readonly string[] = Object.freeze(['b']);
